/**
 * Cart Variant Tier Sync
 *
 * Swaps cart line items to the correct pricing tier (retail ↔ distributor/UC)
 * after a login-state change, and drops recurring plans a customer can't hold
 * (PPC/UFO) back to one-time. The server-side snippet `snippets/cart-variant-sync.liquid`
 * detects mismatches via Liquid (no API call needed) and emits a JSON config;
 * this module reads it and performs the swap via the AJAX Cart API. A line's
 * paired BCRS fee is swapped with it, re-pointed at the new variant + plan.
 *
 * Swap flow — four round trips total, no page reload:
 *   0. One /cart.js read. The config is a RENDER-TIME snapshot; every decision
 *      below is taken against the cart as it is right now (see "Re-runs").
 *   1. One /cart/add.js with an `items` array holding every counterpart line.
 *      If it fails, bail out before any removal — no item is ever lost.
 *   2. One /cart/update.js zeroing every mismatched line by line-item KEY
 *      (`updates: {key: 0}`) and, in the same map, setting each counterpart
 *      line to its intended ABSOLUTE quantity. Keys are stable; line indices
 *      are NOT — /cart/add.js prepends new items, shifting indices captured at
 *      render time. The same request carries `sections` + `sections_url` so the
 *      re-rendered cart markup comes back in the same round trip.
 *   3. Dispatch CartUpdateEvent with the rendered sections — the cart drawer,
 *      cart page, and header bubble refresh the way the theme's own cart
 *      mutations do (see component-cart-items.js updateQuantity and
 *      cart-sync.js notifyCartUi).
 *
 * Re-runs (MA-2906) — the swap is not atomic: the two writes can be cut apart
 * by a navigation, a closed tab, or a second tab running its own (already
 * stale) config. The counterpart lines then stay in the cart while the
 * mismatched originals do too, so the NEXT page renders a config that asks for
 * those counterparts all over again — and /cart/add.js merges identical lines,
 * which is what doubled a 42-item cart to 84. Two properties make a re-run a
 * no-op instead:
 *   - every swapped-in line records the line it replaced in the hidden
 *     `_tier_swap_from` property, so a later run can tell its own leftover from
 *     a line the customer legitimately holds on that variant and skips the add;
 *   - step 2 sets the counterpart's ABSOLUTE quantity rather than trusting the
 *     add, so even a duplicate add (two tabs racing) converges to the intended
 *     quantity instead of accumulating. No cross-tab lock is needed: both tabs
 *     write the same absolute value.
 * A swap whose source line is no longer in the cart is dropped entirely, and
 * quantities come from the live line, never from the snapshot.
 *
 * A sessionStorage attempt guard caps runs at 3 per minute as a backstop
 * against a swap that can never converge (e.g. mis-tagged counterpart
 * metafields) re-triggering on every navigation.
 */

import { fetchConfig, withDistributorIdProperty } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';
import { visibleItemCount } from '@theme/bcrs-fee';

const GUARD_KEY = 'cart-variant-sync-attempts';
const GUARD_WINDOW_MS = 60000;
const GUARD_MAX_ATTEMPTS = 3;

/**
 * Hidden line property naming the cart line a swapped-in line replaced. It is
 * the module's own receipt: it survives navigation, tabs and devices (the cart
 * does), and it is what makes a re-run idempotent. Underscore-prefixed, so the
 * cart and checkout never display it (snippets/cart-products.liquid), and it is
 * rebuilt — never inherited — on each swap, so a line carries at most one.
 */
const SWAP_SOURCE_PROPERTY = '_tier_swap_from';

const configEl = document.getElementById('cart-variant-sync-data');
if (configEl && recordAttempt()) {
  try {
    const { swaps } = JSON.parse(configEl.textContent);
    if (Array.isArray(swaps) && swaps.length > 0) {
      applySwaps(swaps).catch(console.error);
    }
  } catch {
    // Malformed config — skip silently.
  }
}

/**
 * Returns true if we're under the attempt cap for the current window,
 * recording this attempt. Prevents repeat storms if a swap can never converge.
 */
function recordAttempt() {
  let attempts = [];
  try {
    attempts = JSON.parse(sessionStorage.getItem(GUARD_KEY)) || [];
  } catch {
    // Corrupt entry — treat as empty.
  }
  const now = Date.now();
  attempts = attempts.filter((t) => now - t < GUARD_WINDOW_MS);
  if (attempts.length >= GUARD_MAX_ATTEMPTS) {
    console.warn('[cart-variant-sync] attempt cap reached; skipping');
    return false;
  }
  attempts.push(now);
  try {
    sessionStorage.setItem(GUARD_KEY, JSON.stringify(attempts));
  } catch {
    // Storage unavailable (private mode) — proceed without the guard.
  }
  return true;
}

/**
 * Section ids of every cart surface on the page, collected the same way
 * component-cart-items.js builds its `sections` param, so the update response
 * carries re-render markup for each of them.
 */
function collectCartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids);
}

/**
 * The line a previous (interrupted) run already swapped `sourceKey` into, or
 * null when none exists: the line carrying `_tier_swap_from = sourceKey`.
 * @param {Array<{key: string, properties?: Record<string, string> | null}>} lines
 * @param {string} sourceKey
 */
function findSwappedLine(lines, sourceKey) {
  return lines.find((line) => line.properties?.[SWAP_SOURCE_PROPERTY] === sourceKey) ?? null;
}

/** Line properties without the swap receipt, for comparing two lines' identity. */
function propertiesWithoutReceipt(properties) {
  const entries = Object.entries(properties || {}).filter(([name]) => name !== SWAP_SOURCE_PROPERTY);
  return Object.fromEntries(entries);
}

/** @returns {boolean} whether two property sets carry the same names and values. */
function samePropertySet(a, b) {
  const left = propertiesWithoutReceipt(a);
  const right = propertiesWithoutReceipt(b);
  const names = Object.keys(left);
  if (names.length !== Object.keys(right).length) return false;
  return names.every((name) => String(left[name] ?? '') === String(right[name] ?? ''));
}

/**
 * A line the customer already holds that is identical to the counterpart
 * `payload` but for the receipt property — the line /cart/add.js would have
 * merged the counterpart into before the receipt made it distinct. Its quantity
 * is folded into the counterpart and the line itself zeroed, so a swap still
 * leaves ONE row per variant + plan + properties, as the merge used to.
 *
 * @param {Array<object>} lines - live cart lines
 * @param {{id: number, selling_plan?: number, properties: object}} payload
 * @param {Set<string>} excludedKeys - lines this run already spoke for
 */
function findMergeTwin(lines, payload, excludedKeys) {
  const plan = String(payload.selling_plan ?? '');
  return (
    lines.find((line) => {
      if (excludedKeys.has(line.key)) return false;
      // A receipt-bearing line is this module's own leftover, not a twin.
      if (line.properties?.[SWAP_SOURCE_PROPERTY]) return false;
      if (Number(line.variant_id ?? line.id) !== Number(payload.id)) return false;
      if (String(line.selling_plan_allocation?.selling_plan?.id ?? '') !== plan) return false;
      return samePropertySet(line.properties, payload.properties);
    }) ?? null
  );
}

async function applySwaps(swaps) {
  // Step 0 — the live cart. The config was rendered before this page's JS ran
  // (and possibly before an earlier run's writes landed), so it may name lines
  // that are gone, quantities that have changed, and counterparts that already
  // exist. Everything below is decided against `cart`, not against the config.
  const cart = await fetch('/cart.js')
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  if (!Array.isArray(cart?.items)) return;

  const linesByKey = new Map(cart.items.map((line) => [line.key, line]));
  // Lines this run speaks for: every line it may zero, so the twin search below
  // can never pick one of them (or the same twin twice).
  const spokenFor = new Set(swaps.flatMap((swap) => (swap.fee ? [swap.key, swap.fee.key] : [swap.key])));

  /** @type {Array<object>} */
  const items = [];
  /** @type {Record<string, number>} */
  const updates = {};

  for (const swap of swaps) {
    const line = linesByKey.get(swap.key);
    // The mismatched line is gone — the customer removed it, or a previous run
    // already swapped it away. Either way there is nothing left to correct.
    if (!line) continue;

    updates[swap.key] = 0;
    const feeLine = swap.fee ? linesByKey.get(swap.fee.key) : null;
    if (feeLine) updates[feeLine.key] = 0;

    const hasPlan = swap.sellingPlan !== null && swap.sellingPlan !== undefined;
    // The counterpart line, stamped with the key of the line it replaces.
    const counterpart = {
      id: swap.toId,
      quantity: line.quantity,
      properties: {
        ...withDistributorIdProperty(swap.properties || {}, { isRastellis: swap.isRastellis === true }),
        [SWAP_SOURCE_PROPERTY]: swap.key,
      },
    };
    if (hasPlan) counterpart.selling_plan = swap.sellingPlan;

    const twin = findMergeTwin(cart.items, counterpart, spokenFor);
    if (twin) {
      spokenFor.add(twin.key);
      updates[twin.key] = 0;
      counterpart.quantity += twin.quantity;
    }

    // A run cut short between its add and its removal left the counterpart in
    // the cart. Re-adding it is what doubled the cart (MA-2906) — keep the line
    // that is already there and let step 2 settle it at the intended quantity.
    const existing = findSwappedLine(cart.items, swap.key);
    // A receipt naming a variant this swap no longer targets (the customer's
    // tier changed again mid-flight) is not this swap's counterpart: leave it
    // for the next render to flag as a mismatch of its own.
    if (existing && Number(existing.variant_id ?? existing.id) === Number(swap.toId)) {
      spokenFor.add(existing.key);
      updates[existing.key] = counterpart.quantity;
      continue;
    }

    // Step 1 payload. A paired BCRS fee is posted ahead of its parent
    // ([fee, main], as bcrs-fee callers do) so the server orders the pair
    // correctly; both go in the one add.js call, so the fee can never land
    // without its parent.
    if (feeLine) {
      items.push({
        id: swap.fee.variantId,
        quantity: feeLine.quantity,
        properties: {
          ...swap.fee.properties,
          _bcrs_parent_variant_id: String(swap.toId),
          _bcrs_parent_selling_plan: hasPlan ? String(swap.sellingPlan) : '',
        },
      });
    }
    items.push(counterpart);
  }

  if (Object.keys(updates).length === 0) return;

  // Step 1 — one add.js call with every counterpart line still missing.
  if (items.length > 0) {
    const added = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify({ items }) }))
      .then((r) => r.json())
      .catch(() => null);
    // AJAX API error responses carry a status field; abort before any removal
    // so a failed add never strands the customer without their item.
    if (!added || added.status) return;

    // Pin each added counterpart to its intended quantity in step 2. The add
    // merges into any identical line, so a concurrent run in another tab would
    // otherwise leave double the quantity behind; an absolute set converges.
    const addedLines = Array.isArray(added.items) ? added.items : [];
    for (const item of items) {
      const sourceKey = item.properties?.[SWAP_SOURCE_PROPERTY];
      if (!sourceKey) continue;
      const addedLine = findSwappedLine(addedLines, sourceKey);
      if (addedLine?.key) updates[addedLine.key] = item.quantity;
    }
  }

  // Step 2 — one update.js zeroing every mismatched line by key and settling
  // every counterpart at its quantity, with the affected cart sections
  // re-rendered in the same round trip. `keepalive` so a navigation that starts
  // after the request does not strand the cart mid-swap.
  const sectionIds = collectCartSectionIds();
  const body = { updates };
  if (sectionIds.length > 0) {
    body.sections = sectionIds.join(',');
    body.sections_url = window.location.pathname;
  }
  const updated = await fetch(Theme.routes.cart_update_url, {
    ...fetchConfig('json', { body: JSON.stringify(body) }),
    keepalive: true,
  })
    .then((r) => r.json())
    .catch(() => null);
  if (!updated || updated.status) return;

  // Step 3 — notify the cart UI the way the theme's own mutations do. A
  // background correction should never pop the drawer open, so auto-open is
  // parked during the dispatch (cart-sync.js notifyCartUi pattern).
  const autoOpenDrawers = Array.from(document.querySelectorAll('cart-drawer-component[auto-open]'));
  for (const drawer of autoOpenDrawers) drawer.removeAttribute('auto-open');

  document.dispatchEvent(
    new CartUpdateEvent(updated, 'cart-variant-sync', {
      itemCount: visibleItemCount(updated),
      source: 'cart-variant-sync',
      sections: updated.sections ?? undefined,
    })
  );

  setTimeout(() => {
    for (const drawer of autoOpenDrawers) drawer.setAttribute('auto-open', '');
  }, 0);
}
